import type { ReactNode } from 'react'
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CueName } from './cues'
import type { PlayResult, SoundEngine } from './engine'
import { createSoundEngine, isAudioSupported } from './engine'

const STORAGE_KEY = 'ke.sound'

/**
 * The path this page load started on.
 *
 * Captured once, at module scope, so it records where the visitor *landed*
 * rather than wherever they have since navigated to. The title cue belongs
 * to arriving at the home page; reloading on any other route, or clicking
 * through to home from elsewhere in the site, must be silent.
 */
const ENTRY_PATH =
  typeof window === 'undefined' ? '' : window.location.pathname

/** Trailing slashes and the deploy base path are not meaningful here. */
const normalisePath = (path: string): string => {
  const base = import.meta.env.BASE_URL.replace(/\/+$/, '')
  const withoutBase = base && path.startsWith(base) ? path.slice(base.length) : path
  return withoutBase.replace(/\/+$/, '') || '/'
}

/**
 * Elements that should click and tick. Deliberately a selector rather than a
 * prop on every component: sound is a cross-cutting layer, and threading an
 * `onHoverSound` through forty components would couple every one of them to
 * the audio feature for no benefit.
 */
const INTERACTIVE = [
  'a[href]', // links, and every card — cards are links
  'button', // buttons, filter chips, carousel controls
  '[role="button"]',
  '[role="tab"]', // tabs
  '[role="menuitem"]', // menus
].join(',')

/** Opt-out hook for anything that makes its own noise, e.g. the mute button. */
const SILENT = '[data-silent]'

export interface SoundContextValue {
  /** Whether the visitor wants sound. Persisted. */
  enabled: boolean
  /** True once a gesture has unlocked the audio context. */
  ready: boolean
  /** False where the browser has no Web Audio at all. */
  supported: boolean
  /** Schedules a cue, returning when it will sound so visuals can line up. */
  play: (cue: CueName) => PlayResult | null
  /**
   * `performance.now()` at which the title cue began, or `null` if it has
   * not played — because sound is off, blocked, or the visitor has not yet
   * made the gesture that unlocks audio. The intro scene reads this to
   * decide whether to run its long, sound-anchored timeline or its short
   * silent one.
   */
  introAt: number | null
  /**
   * Whether a title cue is still coming.
   *
   * - `pending` — the audio layer is still finding out whether it may play.
   * - `armed` — it may, and the cue is loading. Worth waiting for.
   * - `unavailable` — it may not, or there is nothing to play. Do not wait.
   *
   * The intro scene holds a dark frame while this is `pending` or `armed`
   * and gives up the moment it turns `unavailable`, which is what keeps a
   * visitor whose browser blocks audio from staring at black for a second
   * over a sound they were never going to hear.
   */
  introStatus: IntroStatus
  /**
   * Abandons the title cue for the rest of this page load, so it can never
   * start against a hero image that has already finished arriving.
   */
  cancelIntro: () => void
  /**
   * Asks for the title cue, naming the route it belongs to.
   *
   * Called by the intro scene when it mounts. The cue plays only if this
   * page load actually began on that route — so a reload on `/about` is
   * silent, and so is navigating to home from somewhere else in the site.
   * Honoured once per page load.
   */
  requestIntro: (path: string) => void
  setEnabled: (enabled: boolean) => void
  toggle: () => void
}

export type IntroStatus = 'pending' | 'armed' | 'unavailable'

export const SoundContext = createContext<SoundContextValue | undefined>(
  undefined,
)

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Reads the stored preference.
 *
 * Sound defaults **on**, with one exception: a visitor who has asked their
 * operating system to reduce motion is telling us they want a calmer page,
 * and unrequested audio is the loudest thing on it. They get silence and a
 * visible switch to turn it on.
 */
const readPreference = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'on') return true
    if (stored === 'off') return false
  } catch {
    // Private mode, or storage disabled. Fall through to the default.
  }
  return !prefersReducedMotion()
}

interface SoundProviderProps {
  children: ReactNode
}

/**
 * Owns the audio engine and wires it to the document.
 *
 * ## Starting the title cue on load
 *
 * The cue is meant to start by itself, with the hero image, the moment the
 * page opens. So the provider tries to start audio immediately on mount,
 * with no gesture, and succeeds whenever the browser allows it — which it
 * does for any visitor the browser already associates with this origin
 * (Chrome's media engagement score), for an installed PWA, and for a tab
 * that already had activation.
 *
 * For a genuinely first-time visitor it fails, because browsers block audio
 * until the page has received a click, a tap or a key press. A wheel scroll
 * does not count. This is a platform rule and there is no code that gets
 * around it; pages that appear to autoplay audio were either already
 * trusted, or are muted.
 *
 * When it fails, the gesture listener below picks the cue up on the first
 * real interaction — but only while the intro scene is still waiting for it.
 * Once the scene has given up and faded the hero in silently, the cue is
 * cancelled outright. Hearing a 7-second riser against a picture that has
 * already settled is worse than hearing nothing, because the whole point of
 * the cue is that the two move together. After that, only a page load brings
 * it back.
 *
 * ## Why hover and click are delegated
 *
 * One listener on the document, matching a selector, means every link and
 * button on the site — including ones added later — is covered with no code
 * in the component itself, and the site works identically with the sound
 * feature deleted.
 */
export const SoundProvider = ({ children }: SoundProviderProps) => {
  const engineRef = useRef<SoundEngine | null>(null)
  const [enabled, setEnabledState] = useState(readPreference)
  const [ready, setReady] = useState(false)
  const [introAt, setIntroAt] = useState<number | null>(null)
  const [introStatus, setIntroStatus] = useState<IntroStatus>('pending')
  const supported = useMemo(isAudioSupported, [])

  // The title cue is a one-shot per page load: it belongs to arriving, not
  // to every later gesture, and not to navigating home again from another
  // route. A ref, not state, so it survives re-renders — and it lives above
  // the router, so a client-side navigation cannot reset it.
  const introPending = useRef(true)

  /**
   * Gives up on the title cue for the rest of this page load.
   *
   * Called by the intro scene when it stops waiting and fades the hero in
   * on its own. From here the cue can only come back with a reload.
   */
  const cancelIntro = useCallback(() => {
    introPending.current = false
    setIntroStatus('unavailable')
  }, [])

  /** The route the intro scene asked for the cue on, once it has asked. */
  const introRequest = useRef<string | null>(null)
  /** Whether the engine is unlocked and the cue is decoded. */
  const introLoaded = useRef(false)

  /**
   * Plays the title cue if everything it needs is true at once: the engine
   * can play, the cue is loaded, the intro scene has asked for it, this page
   * load began on the route it asked for, and the visitor has not already
   * scrolled past the moment it belongs to.
   *
   * Called from both sides — the scene may ask before the engine is ready,
   * or the engine may be ready before the scene has asked — so whichever
   * arrives second is the one that fires it.
   */
  const maybePlayIntro = useCallback(() => {
    const engine = engineRef.current
    const wanted = introRequest.current
    if (!engine || !introLoaded.current || !introPending.current) return
    if (wanted === null) return

    // Landed somewhere else. Navigating to home later is not arriving.
    if (normalisePath(ENTRY_PATH) !== normalisePath(wanted)) {
      cancelIntro()
      return
    }

    // Already scrolled into the page: they have left the moment the cue
    // belongs to, and a seven-second riser arriving after them is noise.
    if (window.scrollY >= window.innerHeight * 0.5) {
      cancelIntro()
      return
    }

    introPending.current = false
    const result = engine.play('intro')
    if (result) setIntroAt(result.startsAt)
    // Spent either way. Leaving it 'armed' would make a later mount of the
    // intro scene hold a dark frame waiting for a cue that has been and gone.
    setIntroStatus('unavailable')
  }, [cancelIntro])

  const requestIntro = useCallback(
    (path: string) => {
      // One request per page load, whoever asks.
      if (introRequest.current !== null) return
      introRequest.current = path
      maybePlayIntro()
    },
    [maybePlayIntro],
  )

  const getEngine = useCallback(() => {
    engineRef.current ??= createSoundEngine()
    return engineRef.current
  }, [])

  const play = useCallback(
    (cue: CueName) => {
      if (!enabled || !supported) return null
      return getEngine().play(cue)
    },
    [enabled, supported, getEngine],
  )

  const setEnabled = useCallback(
    (next: boolean) => {
      setEnabledState(next)
      engineRef.current?.setMuted(!next)
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off')
      } catch {
        // Not being able to remember the choice is not a reason to ignore it.
      }
    },
    [],
  )

  const toggle = useCallback(() => {
    const next = !enabled
    const engine = getEngine()

    if (next) {
      // Turning sound on is itself a gesture, so this is a valid moment to
      // unlock — and the confirmation tone has to wait for that to land.
      void engine.unlock().then((ok) => {
        setReady(ok)
        engine.setMuted(false)
        // The confirmation bleep, which is also a sample of what they just
        // switched on.
        if (ok) engine.play('select')
      })
    } else {
      // Acknowledge the press before the mute takes hold, otherwise turning
      // sound off feels like the button did nothing. The reverse sweep is
      // the right shape for it: the kit powering down.
      engine.play('back')
      window.setTimeout(() => engine.setMuted(true), 700)
    }

    setEnabledState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off')
    } catch {
      // See above.
    }
  }, [enabled, getEngine])

  // ---- Download, and try to start without waiting for a gesture ----------
  //
  // Fetching is not gated on user activation, only playback is. Starting the
  // download on mount means the title cue is decoded and ready the moment we
  // are allowed to play it, instead of the riser starting a second late —
  // which is the difference between a cue that is synced to the picture and
  // one that merely follows it.
  //
  // The `unlock()` attempt that follows usually fails, and that is fine. But
  // a browser will allow audio without a fresh gesture when the visitor has
  // engaged with this origin before (Chrome's media engagement score), and
  // for those visitors the intro plays from the first frame and the picture
  // is in sync with no gesture at all. Worth one cheap attempt.
  useEffect(() => {
    if (!supported || !enabled) {
      setIntroStatus('unavailable')
      return
    }
    let cancelled = false

    void (async () => {
      const engine = getEngine()

      // A short deadline: this attempt is speculative and only succeeds for
      // visitors the browser already trusts with audio. A fast "no" matters
      // more than a certain one, because the intro scene is holding a dark
      // frame until it hears back.
      const ok = await engine.unlock(150)
      if (cancelled) return

      if (!ok) {
        // Blocked. The gesture handler below will try again, but the scene
        // should stop waiting now rather than sit dark for a cue that needs
        // a click first.
        setIntroStatus('unavailable')
        void engine.prefetch()
        return
      }

      setIntroStatus('armed')

      // The title cue first and on its own. The other five are small and can
      // arrive whenever; this one gates the picture.
      await engine.load('intro')
      if (cancelled) return

      setReady(true)
      void engine.prefetch()

      introLoaded.current = true
      maybePlayIntro()
    })()

    return () => {
      cancelled = true
    }
  }, [supported, enabled, getEngine, maybePlayIntro])

  // ---- Unlock on the first gesture, then play the title cue --------------
  useEffect(() => {
    if (!supported || ready) return

    // Which events actually grant user activation.
    //
    // `pointerdown` and `touchstart` do **not**, on touch. A tap fires
    // pointerdown → touchstart → touchend → pointerup → click, and only the
    // last three carry activation. Listening for the first two meant every
    // tap on a phone tried to unlock before the browser would allow it,
    // failed, and — because the listeners were registered `once` — removed
    // themselves. The gate could never be opened on mobile at all.
    //
    // So: the full set, and no `once`. A gesture that fails to unlock must
    // not consume the chance; the listeners come off when `ready` flips,
    // which is the only thing that proves it worked.
    const events = [
      'pointerdown', // mouse
      'pointerup', // pen and touch
      'touchend', // older iOS
      'click', // the reliable catch-all
      'keydown',
    ] as const

    // Every event gets its own attempt, with a short deadline.
    //
    // An earlier version guarded against overlapping attempts with a flag.
    // That was worse than the problem: `pointerdown` cannot grant activation
    // but still started an attempt, `resume()` then sat pending — Chromium
    // never rejects it, it just waits — and the flag stayed set, so the
    // `pointerup` and `touchend` that *would* have unlocked were dropped.
    // Every tap on a phone failed.
    //
    // `unlock()` is idempotent, so letting the events race is both simpler
    // and correct: whichever one carries activation is the one that wins.
    const onGesture = () => {
      const engine = getEngine()
      engine.setMuted(!enabled)

      void engine.unlock(250).then(async (ok) => {
        if (!ok) return
        setReady(true)
        if (introPending.current && enabled) {
          setIntroStatus('armed')
          await engine.load('intro')

          // Re-check after the await: the scene may have given up while the
          // file was loading, and `cancelIntro` is the authority on that.
          if (!introPending.current) return

          introLoaded.current = true
          maybePlayIntro()
        }
      })
    }

    events.forEach((type) =>
      window.addEventListener(type, onGesture, { passive: true }),
    )
    return () => {
      events.forEach((type) => window.removeEventListener(type, onGesture))
    }
  }, [supported, ready, enabled, getEngine, maybePlayIntro])

  // ---- Delegated hover and click ----------------------------------------
  useEffect(() => {
    if (!enabled || !ready) return

    const engine = getEngine()
    let lastHovered: Element | null = null

    /**
     * The element a pointer event should be attributed to.
     *
     * Resolves to the **outermost** interactive ancestor, not the nearest
     * one. Interactive elements nest here — a project card is a link with a
     * button-shaped arrow inside it, a domain card is a link wrapping a list
     * of chips — and `closest()` alone returns the innermost, so moving the
     * pointer from a card onto its own arrow counted as arriving somewhere
     * new and fired a second tick. One hover, one sound.
     */
    const target = (event: Event) => {
      const node = event.target
      if (!(node instanceof Element)) return null

      let match = node.closest(INTERACTIVE)
      if (!match) return null

      for (
        let outer = match.parentElement?.closest(INTERACTIVE);
        outer;
        outer = outer.parentElement?.closest(INTERACTIVE) ?? null
      ) {
        match = outer
      }

      if (match.closest(SILENT)) return null
      if (
        match.hasAttribute('disabled') ||
        match.getAttribute('aria-disabled') === 'true'
      ) {
        return null
      }
      return match
    }

    /**
     * Entering something interactive.
     *
     * One sound per element, however far the pointer then travels inside it
     * — across the label, onto the arrow, over a nested chip. `target()`
     * resolves to the outermost interactive ancestor, so every one of those
     * is the same element, and an element that is already the current one is
     * ignored.
     */
    const onOver = (event: PointerEvent) => {
      // Touch fires a synthetic hover immediately before the tap; playing
      // both would double every press on a phone.
      if (event.pointerType !== 'mouse') return
      const match = target(event)
      if (!match || match === lastHovered) return
      lastHovered = match
      engine.play('hover')
    }

    /**
     * Leaving one — and *only* leaving.
     *
     * `pointerout` also fires when the pointer moves from an element onto
     * its own child, which is not leaving anything. Clearing the state there
     * meant the next `pointerover` looked like a fresh arrival and played a
     * second tick, so moving across a card sounded once per element inside
     * it. Nothing is cleared unless the pointer has genuinely gone somewhere
     * outside.
     *
     * There is no sound on the way out. Leaving is not an event the visitor
     * did on purpose.
     */
    const onOut = (event: PointerEvent) => {
      if (!lastHovered) return
      const next = event.relatedTarget
      if (next instanceof Node && lastHovered.contains(next)) return
      lastHovered = null
    }

    const onDown = (event: PointerEvent) => {
      if (event.button !== 0) return
      if (!target(event)) return
      engine.play('click')
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      const node = document.activeElement
      if (!(node instanceof Element)) return
      const match = node.closest(INTERACTIVE)
      if (!match || match.closest(SILENT)) return
      engine.play('click')
    }

    // Keyboard focus deserves the same tick as a hover: tabbing through the
    // navigation should feel like moving through it, not like nothing.
    const onFocus = (event: FocusEvent) => {
      const node = event.target
      if (!(node instanceof Element)) return
      // Only for keyboard focus — a click already made its own sound.
      if (!node.matches(':focus-visible')) return
      const match = node.closest(INTERACTIVE)
      if (!match || match.closest(SILENT)) return
      lastHovered = match
      engine.play('hover')
    }

    document.addEventListener('pointerover', onOver, { passive: true })
    document.addEventListener('pointerout', onOut, { passive: true })
    document.addEventListener('pointerdown', onDown, { passive: true })
    document.addEventListener('keydown', onKey, { passive: true })
    document.addEventListener('focusin', onFocus, { passive: true })

    return () => {
      document.removeEventListener('pointerover', onOver)
      document.removeEventListener('pointerout', onOut)
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('focusin', onFocus)
    }
  }, [enabled, ready, getEngine])

  // ---- Go quiet in a background tab -------------------------------------
  useEffect(() => {
    const onVisibility = () => {
      engineRef.current?.setMuted(document.hidden || !enabled)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [enabled])

  useEffect(() => () => engineRef.current?.dispose(), [])

  const value = useMemo<SoundContextValue>(
    () => ({
      enabled,
      ready,
      supported,
      play,
      introAt,
      introStatus,
      cancelIntro,
      requestIntro,
      setEnabled,
      toggle,
    }),
    [
      enabled,
      ready,
      supported,
      play,
      introAt,
      introStatus,
      cancelIntro,
      requestIntro,
      setEnabled,
      toggle,
    ],
  )

  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>
}
